import { Component, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalOverlayRef } from '@core/classes/modal-overlay.ref';
import { Currency, DataTypeOptions, SavingOrInvestment } from '@core/constants/data-types.constants';
import { FuturePlanData, InsurancesData, LifePlan, SimpleInsurance } from '@core/models/life-plan.model';
import { ConfigService } from '@core/services/config.service';
import { LifePlanManagerService } from '@core/services/life-plan-manager.service';
import { LiteService } from '@core/services/lite.service';
import { ReportsService } from '@core/services/reports.service';
import { UiService } from '@core/services/ui.service';
import { Store } from '@ngrx/store';
import * as actions from '@redux/actions';
import { AppState } from '@redux/app.reducers';
import { CustomValidators } from '@shared/utils/forms/validations.util';
import { calculateGoalYearsLimits } from '@shared/utils/functions.utils';
import { formatISO, parseISO } from 'date-fns';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'mfp-saving-goals',
  templateUrl: './saving-goals.component.html',
  styleUrls: ['./saving-goals.component.scss']
})
export class SavingGoalsComponent implements OnDestroy {
  sgForm: FormGroup;
  currencyOptions: DataTypeOptions<Currency> = [];
  futurePlansOptions: DataTypeOptions<SavingOrInvestment> = [];
  goalYearsLimit: any;

  $storeSubscription: Subscription;

  futurePlans?: FuturePlanData[];

  lifePlan!: LifePlan;
  userInsurances! : SimpleInsurance[];
  spouseInsurances : InsurancesData | undefined;

  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: ModalOverlayRef,
    private store: Store<AppState>,
    private configService: ConfigService,
    private reportsService: ReportsService,
    private lifePlanManager: LifePlanManagerService,
    private uiService: UiService,
    private liteService: LiteService
  ) {
    this.goalYearsLimit = calculateGoalYearsLimits();
    this.sgForm = this.formBuilder.group({
      futurePlans: new FormArray([])
    });
    this.$storeSubscription = this.store.subscribe({
      next: state => {
        this.currencyOptions = state.config.currencies;
        this.futurePlansOptions = state.config.futurePlans;
        this.lifePlan = state.lifePlan.currentLifePlan;
        this.userInsurances = this.lifePlan.user?.insurances?.currentInsurances! ?? [];
        this.spouseInsurances = this.lifePlan.spouse?.insurances;
        this.setFuturePlans(state.lifePlan.currentLifePlan.user?.financial?.futurePlans);
      }
    });
  }

  updateSavingProductGoals() {
    const savingGeneralValues = this.liteService.calculateGeneralSavingValues(this.lifePlan);
    const currentInsurances : SimpleInsurance[] = JSON.parse(JSON.stringify(this.userInsurances.filter(i => i.type !== "SAVING")));
    currentInsurances.push({type: "SAVING", assuredAmount: savingGeneralValues.savings});

    const userInsurance : InsurancesData = { currentInsurances : currentInsurances};
    this.store.dispatch(
      actions.completedLifePlanInsurancesDataEdit({
        userInsurancesData: userInsurance,
        spouseInsurancesData: this.spouseInsurances
      })
    );
    this.store.dispatch(actions.updateInBackgroundLifePlan())
  }

  updateValuesProductGoals() {
    const savingGeneralValues = this.liteService.calculateGeneralSavingValues(this.lifePlan);
    const savingGeneralProduct = this.lifePlan.insurances!['saving-general'];

    this.liteService.calculateTargetWithLimits(savingGeneralProduct, savingGeneralValues.assuredAmount.amount, savingGeneralValues.fee.amount).subscribe({
      next: liteResponses => {
        this.store.dispatch(
          actions.updateLifePlanInsuranceTargetCurrentProductWithLiteValuesAndLimits({
            insuranceTargetId: savingGeneralProduct.id,
            productId: savingGeneralProduct.currentProduct?.id || '',
            liteResponses
          })
        );
        this.store.dispatch(actions.updateInBackgroundLifePlan());
      },
      error: error => {
        this.uiService.openErrorModal();
      }
    });
  }

  ngOnDestroy(): void {
    this.$storeSubscription.unsubscribe();
  }

  close() {
    this.dialogRef.close();
  }

  save() {
    this.sgForm.markAllAsTouched();
    if (this.sgForm.valid) {
      this.uiService.presentLoading();
      this.store.dispatch(
        actions.completedFuturePlansEdit({
          futurePlans: this.getFuturePlans()
        })
      );
      this.reportsService
        .getAndUpdateReportsData(this.lifePlan)
        .pipe(switchMap(() => this.lifePlanManager.updateAndManageLifePlan(this.lifePlan)))
        .subscribe({
          error: error => {
            this.uiService.dismissLoading();
            this.uiService.openErrorModal();
          },
          complete: () => {
            this.updateSavingProductGoals();
            this.updateValuesProductGoals();

            this.uiService.dismissLoading();
            this.close();
          }
        });
    }
  }

  closeAndSave() {
    this.uiService.presentLoading();
    this.store.dispatch(
      actions.completedFuturePlansEdit({
        futurePlans: []
      })
    );
    this.reportsService
      .getAndUpdateReportsData(this.lifePlan)
      .pipe(switchMap(() => this.lifePlanManager.updateAndManageLifePlan(this.lifePlan)))
      .subscribe({
        error: error => {
          this.uiService.dismissLoading();
          this.uiService.openErrorModal();
        },
        complete: () => {
          this.updateSavingProductGoals();
          this.updateValuesProductGoals();

          this.uiService.dismissLoading();
          this.close();
        }
      });
  }

  get futurePlansFormArray(): FormArray {
    return this.sgForm.controls.futurePlans as FormArray;
  }

  public createFuturePlansDefaultValue = () => {
    return new FormGroup({
      id: new FormControl(''),
      goalAmount: new FormControl(this.configService.getDefaultCurrencyAmount(), CustomValidators.requiredComplete),
      assuredAmount: new FormControl(this.configService.getDefaultCurrencyAmount(), CustomValidators.requiredComplete),
      type: new FormControl('', Validators.required),
      startDate: new FormControl(),
      goalDate: new FormControl('', [
        Validators.required,
        Validators.min(this.goalYearsLimit.min),
        Validators.max(this.goalYearsLimit.max)
      ])
    });
  };

  private setFuturePlans(futurePlans: FuturePlanData[] | undefined) {
    this.futurePlans = futurePlans;
    this.futurePlansFormArray.clear();
    if (futurePlans && futurePlans.length > 0) {
      futurePlans
        ?.map((fp: FuturePlanData) => {
          const relatedForm = this.createFuturePlansDefaultValue();
          relatedForm.controls.id.setValue(fp.id);
          relatedForm.controls.type.setValue(fp.type);
          relatedForm.controls.goalAmount.setValue(fp.goalAmount);
          relatedForm.controls.assuredAmount.setValue(fp.savingAmount);
          relatedForm.controls.startDate.setValue(fp.startDate);
          relatedForm.controls.goalDate.setValue(parseISO(fp.goalDate).getFullYear());
          return relatedForm;
        })
        .forEach((rf: FormGroup) => this.futurePlansFormArray.push(rf));
    } else {
      this.futurePlansFormArray.push(this.createFuturePlansDefaultValue());
    }
  }

  private getFuturePlans(): FuturePlanData[] {
    const futurePlans = this.futurePlansFormArray.value as any[];
    futurePlans.forEach(fp => {
      fp.id = fp.id || uuidv4();
      fp.startDate = fp.startDate ?? formatISO(new Date(), { representation: 'date' });
      fp.goalDate = formatISO(new Date(fp.goalDate, 0, 1), { representation: 'date' });
      fp.savingAmount = fp.assuredAmount;
      fp.assuredAmount = {amount: fp.goalAmount.amount - fp.assuredAmount.amount, currency: "EUR"};
    });
    return futurePlans;
  }
}
